import React, { memo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useOnclickOutside from "react-cool-onclickoutside";
import { useTranslation } from "react-i18next";
import { HeaderLanguageIcon } from "../../../assets/icons/header_icons";
// Three dots only in the phone's bottom bar, as the prototype has it — there the button
// is labelled "Ще" and the glyph does not carry the meaning alone. Every wider layout
// shows the icon bare against an empty strip, where three thin dots read as almost
// nothing, so those get the burger instead.
import { DotsThreeIcon, LifebuoyIcon, ListIcon } from "@phosphor-icons/react";
import HeaderAboutIcon from "../../../assets/icons/header_icons/header_about_icon";
import { ArrowIcon } from "../../../assets/icons";
import LogoutIcon from "../../../assets/icons/user_profile_page_icons/logout_icon";
import { useUserDataContext } from "../../../../user_data_context";
import { useAppContext } from "../../../../app_context";
import { useLogout } from "../../../../hooks/use_logout";
import useWindowBreakpoint from "../../../../hooks/use_window_breakpoint";
import { APPLICATION_ROUTES } from "../../../utils/routes";
import { useNavSections } from "../use_nav_sections";
import { useSidebarFlag } from "../../../../hooks/use_sidebar_flag";
import ProfileCard from "./profile_card";
import NavSection, {
	ItemRow,
	NavItemRow,
	ItemLabel,
	SectionItems,
	ItemTrailingIcon,
} from "./nav_section";

const SECTIONS_GROUP_CLASS = "flex flex-col gap-[8px]";

const SIGN_OUT_CLASS = [
	"flex items-center justify-center gap-[12px] w-full h-[46px] px-[12px]",
	"rounded-md bg-error/8 text-error text-[14px] font-medium cursor-pointer transition-all duration-200 mt-auto",
	"hover:bg-[#fcebec] dark:hover:bg-[rgba(220,38,38,0.12)]",
].join(" ");

const MORE_MENU_SCROLL_CLASS = "overflow-y-auto flex flex-col flex-1 min-h-0";

const MORE_MENU_BODY_CLASS = [
	"p-[14px_12px_24px] flex flex-col gap-[18px] flex-1",
	"max-[748px]:w-full max-[748px]:max-w-[392px] max-[748px]:mx-auto",
].join(" ");

const ITEM_ARROW_CLASS = "rotate-180 mt-[1px] w-[10px] h-[10px]";

const getContainerClass = isTabletMenu =>
	[
		"absolute z-[9999] w-[392px] max-h-[80vh]",
		isTabletMenu
			? "top-[calc(100%+15px)] right-0 bottom-auto left-auto"
			: "top-auto right-auto left-[calc(100%+10px)] bottom-0",
		"bg-white dark:bg-dark-card rounded-[14px] [box-shadow:4.462px_4.462px_7.808px_0_rgba(0,0,0,0.12)] flex flex-col overflow-hidden",
		// The offsets below are the bar's own height at each width, so the panel stands on
		// top of it rather than under it. They follow .app-header-container: 70 / 64.
		"max-[1024px]:top-auto max-[1024px]:bottom-[70px] max-[1024px]:max-h-[calc(100vh-95px)]",
		"max-[1024px]:[html.pwa-ios_&]:bottom-[103px] max-[1024px]:[html.pwa-ios_&]:max-h-[calc(100vh-128px)]",
		"max-[748px]:fixed max-[748px]:top-0 max-[748px]:left-0 max-[748px]:w-full max-[748px]:h-[calc(100%-70px)] max-[748px]:max-h-none max-[748px]:rounded-none max-[748px]:border-b max-[748px]:border-b-border max-[748px]:dark:border-b-white/10 max-[748px]:[box-shadow:none] max-[748px]:z-[99]",
		"max-[748px]:[html.pwa-ios_&]:h-[calc(100%-103px)]",
		"max-[500px]:h-[calc(100%-64px)]",
		"max-[500px]:[html.pwa-ios_&]:h-[calc(100%-103px)]",
	].join(" ");

// In the phone's bottom bar this button is one destination among the others, so it
// takes their shape: icon over label, no pill, the row's own inactive colour. The
// wider layouts keep the pill the menu has always opened with.
const STACKED_MORE_BUTTON_CLASS = [
	// w-full because a <button> sizes to its content even as a block: without it the
	// glyph would sit at the left edge of its slot instead of centred in it.
	"flex w-full flex-1 flex-col items-center justify-center gap-[2px]",
	"px-0 pt-[6px] pb-[4px] select-none cursor-pointer bg-transparent",
	"transition-colors duration-200 [-webkit-tap-highlight-color:transparent]",
	"[&_svg]:w-[21px] [&_svg]:h-[21px] [&_svg]:[flex:0_0_auto]",
].join(" ");

const getMoreButtonClass = (isOpen, isMobile) => {
	if (isMobile) {
		return [
			STACKED_MORE_BUTTON_CLASS,
			isOpen ? "text-primary" : "text-nav-inactive dark:text-text-dark-muted",
		].join(" ");
	}
	// Full opacity throughout. The closed button used to sit at 50% below 1024px, which
	// left it visibly fainter than the nav icons beside it — it is a destination like
	// they are, not a secondary control.
	return [
		"flex items-center gap-[7px] h-[38px] px-[16px] text-[14px] leading-[140%] select-none text-primary cursor-pointer transition-all duration-200",
		// Phosphor draws at 1em, which here would be the 14px label; the icon it replaced
		// carried 21px of its own.
		"[&_svg]:w-[21px] [&_svg]:h-[21px]",
		isOpen ? "rounded-[25px] bg-primary/15" : "",
		isOpen ? "max-[1024px]:ml-[10px]" : "",
	]
		.filter(Boolean)
		.join(" ");
};

const HeaderMoreMenu = ({
	unreadAnswersCount,
	showAdminButton,
	onOpenSettings,
	onOpenSupport,
	isSettingsModalOpen = false,
	forceTopHeader = false,
}) => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const moreMenuRef = useRef();
	const logout = useLogout();

	const { userData, accessRights } = useUserDataContext();
	const { isHeaderMoreMenuOpen, setIsHeaderMoreMenuOpen } = useAppContext();

	const isTablet = useWindowBreakpoint(1024);
	// Below 500px the header is the phone's bottom bar (see header_menu_item).
	const isMobile = useWindowBreakpoint(500);

	const isAuthenticated = !!userData?._id;

	const [isProfileExpanded, setIsProfileExpanded] = useSidebarFlag("profileExpanded");
	const [isAdminExpanded, setIsAdminExpanded] = useSidebarFlag("adminExpanded");

	const { profileItems, adminItems: allowedAdminItems } = useNavSections(
		accessRights,
		isAuthenticated,
	);
	const adminItems = showAdminButton ? allowedAdminItems : [];

	const closeMenu = useCallback(() => {
		setIsHeaderMoreMenuOpen(false);
	}, [setIsHeaderMoreMenuOpen]);

	const moreButtonClickHandler = useCallback(() => {
		setIsHeaderMoreMenuOpen(prevState => !prevState);
	}, [setIsHeaderMoreMenuOpen]);

	useOnclickOutside(closeMenu, { refs: [moreMenuRef], disabled: isSettingsModalOpen });

	const handleEditProfile = useCallback(
		e => {
			e.stopPropagation();
			onOpenSettings?.("GENERAL");
		},
		[onOpenSettings],
	);

	const handleOpenLanguage = useCallback(
		e => {
			e.stopPropagation();
			onOpenSettings?.("LANGUAGE");
		},
		[onOpenSettings],
	);

	// The menu closes first: the support window is a modal of its own, not a page under it.
	const handleOpenSupport = useCallback(
		e => {
			e.stopPropagation();
			closeMenu();
			onOpenSupport?.();
		},
		[closeMenu, onOpenSupport],
	);

	const handleOpenAboutPage = useCallback(
		e => {
			e.stopPropagation();
			navigate(APPLICATION_ROUTES.ABOUT);
			closeMenu();
		},
		[navigate, closeMenu],
	);

	const handleNavItemClick = useCallback(() => {
		closeMenu();
	}, [closeMenu]);

	const handleSignOut = useCallback(async () => {
		closeMenu();
		await logout();
	}, [closeMenu, logout]);

	const isPathActive = useCallback(
		path => {
			if (!path) return false;
			if (path === "/") return location.pathname === "/";
			return location.pathname === path || location.pathname.startsWith(path + "/");
		},
		[location.pathname],
	);

	const aboutPageIsOpen = isPathActive(APPLICATION_ROUTES.ABOUT);

	return (
		// flex-1 on the phone so this button takes the same slot as the nav items beside
		// it — the bar lays all five out itself there (see MainNavContainer).
		<div className="relative max-xs:flex-1" ref={moreMenuRef}>
			<button
				className={getMoreButtonClass(isHeaderMoreMenuOpen, isMobile)}
				onClick={moreButtonClickHandler}
				onMouseDown={e => e.stopPropagation()}
				data-testid="more-menu-button"
			>
				{isMobile ? <DotsThreeIcon /> : <ListIcon />}
				{(isMobile || (isTablet && isHeaderMoreMenuOpen)) && (
					<span
						data-testid="more-menu-button-text"
						className={
							isMobile
								? `text-[10.5px] leading-[normal] ${
										isHeaderMoreMenuOpen ? "font-semibold" : "font-medium"
									}`
								: undefined
						}
					>
						{t("app-header.more-btn")}
					</span>
				)}
			</button>

			{isHeaderMoreMenuOpen && (
				<div
					className={getContainerClass(isTablet || forceTopHeader)}
					onMouseDown={e => e.stopPropagation()}
				>
					<div className={MORE_MENU_SCROLL_CLASS}>
						<div className={MORE_MENU_BODY_CLASS} data-testid="main-more-menu">
							{!forceTopHeader && (
								<ProfileCard
									userData={userData}
									onEditProfile={handleEditProfile}
									onLoginClick={handleNavItemClick}
									t={t}
								/>
							)}

							{(profileItems.length > 0 || (adminItems.length > 0 && isTablet)) && (
								<div className={SECTIONS_GROUP_CLASS}>
									{!!profileItems.length && (
										<NavSection
											title={t("app-header.sidebar.section-profile")}
											isExpanded={isProfileExpanded}
											onToggle={() => setIsProfileExpanded(prev => !prev)}
											testId="profile-section"
										>
											{profileItems.map(item => {
												const Icon = item.icon;
												const isActive = isPathActive(item.path);
												const iconColor = isActive
													? "var(--color-primary)"
													: "var(--color-icon-default)";
												return (
													<NavItemRow
														key={item.key}
														to={item.path}
														onClick={handleNavItemClick}
														data-testid={`more-menu-${item.key}`}
													>
														<Icon fill={iconColor} stroke={iconColor} />
														<ItemLabel>{t(item.textKey)}</ItemLabel>
														{item.key === "profile-questions" && unreadAnswersCount > 0 && (
															<span>{unreadAnswersCount}</span>
														)}
													</NavItemRow>
												);
											})}
										</NavSection>
									)}

									{!!adminItems.length && isTablet && (
										<NavSection
											title={t("app-header.sidebar.section-admin")}
											isExpanded={isAdminExpanded}
											onToggle={() => setIsAdminExpanded(prev => !prev)}
											testId="admin-section"
										>
											{adminItems.map(item => {
												const Icon = item.icon;
												const isActive = isPathActive(item.path);
												const iconColor = isActive
													? "var(--color-primary)"
													: "var(--color-icon-default)";
												return (
													<NavItemRow
														key={item.key}
														to={item.path}
														onClick={handleNavItemClick}
														data-testid={`more-menu-${item.key}`}
													>
														<Icon fill={iconColor} stroke={iconColor} />
														<ItemLabel>{t(item.textKey)}</ItemLabel>
													</NavItemRow>
												);
											})}
										</NavSection>
									)}
								</div>
							)}

							<SectionItems>
								{/* Signed in only: the answer arrives in the profile's Questions tab. */}
								{isAuthenticated && (
									<ItemRow
										type="button"
										onClick={handleOpenSupport}
										data-testid="more-menu-support"
									>
										<LifebuoyIcon color="var(--color-icon-default)" />
										<ItemLabel>{t("app-header.sidebar.support")}</ItemLabel>
									</ItemRow>
								)}

								<ItemRow
									type="button"
									onClick={handleOpenLanguage}
									data-testid="more-menu-language"
								>
									<HeaderLanguageIcon
										fill="var(--color-icon-default)"
										stroke="var(--color-icon-default)"
									/>
									<ItemLabel>{t("app-header.sidebar.language")}</ItemLabel>
									<ItemTrailingIcon>
										<ArrowIcon fill="var(--color-text-secondary)" className={ITEM_ARROW_CLASS} />
									</ItemTrailingIcon>
								</ItemRow>

								<ItemRow
									type="button"
									onClick={handleOpenAboutPage}
									$active={aboutPageIsOpen}
									data-testid="more-menu-about"
								>
									<HeaderAboutIcon />
									<ItemLabel data-testid="about-menu-title">{t("app-header.about")}</ItemLabel>
								</ItemRow>
							</SectionItems>

							{isAuthenticated && (
								<button
									className={SIGN_OUT_CLASS}
									onClick={handleSignOut}
									data-testid="more-menu-sign-out"
								>
									<LogoutIcon fill="var(--color-error)" />
									{t("user-profile-page.general.logout-btn")}
								</button>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default memo(HeaderMoreMenu);
